import { Link } from "react-router-dom";
import {
  ArrowRight,
  ShieldCheck,
  Users,
  BadgeCheck,
  Landmark,
  Wallet,
  BarChart3,
  LockKeyhole,
  CheckCircle2,
  Building2,
  Target,
  Eye,
  TrendingUp,
  FileCheck2,
} from "lucide-react";

import aboutImg from "../assets/img/about.png";
import growthImg from "../assets/img/growth-success3.png";
import planningImg from "../assets/img/financial-planning1.png";
import "../assets/css/about.css";

const coreServices = [
  "Salary advance loans for government workers",
  "Collateral-backed lending for vehicles and property",
  "Peer-to-peer investment structuring",
  "Financial services and advisory",
];

const values = [
  {
    icon: ShieldCheck,
    title: "Transparency",
    text: "Clear lending structures, monitored repayment systems, and open communication.",
  },
  {
    icon: LockKeyhole,
    title: "Risk Control",
    text: "Strict borrower screening, collateral support, and disciplined repayment monitoring.",
  },
  {
    icon: BarChart3,
    title: "Growth",
    text: "We help borrowers access finance while investors earn stable returns.",
  },
];

const modelSteps = [
  "Investors provide capital",
  "GMT deploys funds to carefully vetted borrowers",
  "Borrowers repay through structured systems",
  "Investors receive fixed monthly returns",
];

export default function About() {
  return (
    <main className="about-fintech-page">
      <section className="about-hero">
        <div className="about-hero-copy">
          <span>About GMT Technologies</span>
          <h1>Technology-driven lending built for access, trust and growth</h1>
          <p>
            GMT Technologies Limited is a fast-growing financial technology
            company delivering structured peer-to-peer lending, salary advance
            loans, collateral-backed lending, and financial advisory.
          </p>

          <div className="about-hero-actions">
            <Link to="/investment" className="about-primary-btn">
              View Investment Plans <ArrowRight size={16} />
            </Link>

            <Link to="/contact" className="about-secondary-btn">
              Contact Us <ArrowRight size={16} />
            </Link>
          </div>
        </div>

        <div className="about-hero-image">
          <img src={aboutImg} alt="GMT Technologies financial technology office" />

          <div className="about-floating-card">
            <strong>P2P Lending</strong>
            <span>Technology-driven finance</span>
          </div>
        </div>
      </section>

      <section className="about-summary-grid">
        <div className="about-summary-card">
          <Wallet />
          <h3>Salary Advance Loans</h3>
          <p>Designed for government workers with predictable repayment.</p>
        </div>

        <div className="about-summary-card active">
          <Landmark />
          <h3>Collateral-Backed Lending</h3>
          <p>Vehicle and property-backed lending to protect capital.</p>
        </div>

        <div className="about-summary-card">
          <TrendingUp />
          <h3>Investor Partnership</h3>
          <p>Structured investment model with stable monthly returns.</p>
        </div>
      </section>

      <section className="about-story-section">
        <div className="about-story-image">
          <img src={growthImg} alt="Structured lending and financial growth" />
        </div>

        <div className="about-story-copy">
          <span>Our Story</span>
          <h2>We identified a critical gap in access to quick finance</h2>

          <p>
            At GMT Technologies, we saw the need for quick and reliable finance,
            especially among salary earners and small business owners.
          </p>

          <p>
            By leveraging technology and structured repayment systems, we built a
            model that supports fast loan disbursement, controlled risk exposure,
            and reliable repayment structures.
          </p>

          <ul>
            {coreServices.map((item) => (
              <li key={item}>
                <CheckCircle2 size={18} />
                {item}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="mission-vision-section">
        <div className="mission-card">
          <div className="mission-icon">
            <Target />
          </div>

          <span>Mission Statement</span>

          <h3>
            To simplify access to finance by leveraging innovative technology.
          </h3>

          <p>
            We deliver fast, reliable, and inclusive lending solutions for
            individuals and businesses.
          </p>
        </div>

        <div className="mission-card dark">
          <div className="mission-icon">
            <Eye />
          </div>

          <span>Vision Statement</span>

          <h3>To become a leading peer-to-peer lending technology company.</h3>

          <p>
            We aim to drive financial inclusion and transform how individuals
            and businesses access capital across Africa and beyond.
          </p>
        </div>
      </section>

      <section className="business-model-section">
        <div className="business-model-copy">
          <span>Business Model</span>
          <h2>A simple, effective and structured lending model</h2>
          <p>
            Our model connects investors seeking stable returns with carefully
            vetted borrowers who need reliable access to finance.
          </p>
        </div>

        <div className="business-model-grid">
          {modelSteps.map((step, index) => (
            <div className="business-model-card" key={step}>
              <strong>0{index + 1}</strong>
              <p>{step}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="about-values-section">
        <div className="about-section-heading">
          <span>Why We Stand Out</span>
          <h2>
            Built on technology, transparency and disciplined risk management
          </h2>
        </div>

        <div className="about-values-grid">
          {values.map((item) => {
            const Icon = item.icon;

            return (
              <div className="about-value-card" key={item.title}>
                <Icon />
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="compliance-section">
        <div className="compliance-copy">
          <span>Regulatory & Compliance</span>
          <h2>Operating with discipline, accountability and trust</h2>
          <p>
            GMT Technologies Limited operates as a financial technology and
            lending service provider within defined lending activities. All
            investments are structured as private investment agreements.
          </p>

          <div className="compliance-list">
            <div>
              <BadgeCheck />
              CAC registered company
            </div>

            <div>
              <ShieldCheck />
              Applicable financial service compliance
            </div>

            <div>
              <FileCheck2 />
              Private investment agreement structure
            </div>

            <div>
              <Building2 />
              Technology-driven lending operations
            </div>
          </div>
        </div>

        <div className="compliance-image">
          <img src={planningImg} alt="Compliance and financial planning" />
        </div>
      </section>
    </main>
  );
}